/**
 * Buildings added and edited in eMerge (AUTHORITY RULING, Isaiah Cotto,
 * 2026-10-05): backend/src/services/scheduling/buildings.js behind
 * /api/v1/scheduling/buildings. eMerge is the authority for every operational
 * field of every building; Monday is provenance only.
 */
import api from '../../services/api';
import type { ApiResult } from '../finance/financeApi';

export interface BuildingType { code: string; name: string }

/**
 * The practices eMerge covers at a building, for Scheduling (migration 20261104f). Not the clinical gate: adding
 * Podiatry here opens no Podiatry charting (that stays its own activation).
 */
export type PracticeCode = 'AUDIOLOGY' | 'PODIATRY' | 'OPTOMETRY' | 'DENTISTRY';
export const PRACTICE_CODES: PracticeCode[] = ['AUDIOLOGY', 'PODIATRY', 'OPTOMETRY', 'DENTISTRY'];
export const PRACTICE_LABEL: Record<PracticeCode, string> = { AUDIOLOGY: 'Audiology', PODIATRY: 'Podiatry', OPTOMETRY: 'Optometry', DENTISTRY: 'Dentistry' };
/** "Audiology, Podiatry" in the vocabulary's order; "no practice recorded" for none. */
export const practiceWords = (codes: readonly string[] | null | undefined): string => {
  const list = PRACTICE_CODES.filter((c) => (codes || []).includes(c)).map((c) => PRACTICE_LABEL[c]);
  return list.length ? list.join(', ') : 'no practice recorded';
};
/** " (Audiology, Podiatry)" for a notice; "" when the server sent no practices (an older server), never a guess. */
export const practiceNote = (codes: readonly string[] | null | undefined): string => (codes && codes.length ? ` (${practiceWords(codes)})` : '');

export interface BuildingMeta {
  /** `practices`: add or end a practice at a building (FACILITY_NATIVE_WRITES `practices`). Absent before 20261104f. */
  can: { create: boolean; edit: boolean; practices?: boolean };
  /** The practices offered, with their names, and the one a building with none recorded reads as. Absent before 20261104f. */
  practices?: { code: PracticeCode; name: string }[];
  default_practice?: PracticeCode;
  write_groups: string[];
  enterprise: boolean;
  /** Why nothing is possible here, in words; null when something is. */
  why: string | null;
  types: BuildingType[];
  /** The business-status words (closed list), and the regions and credentialing words in use (suggestions). */
  vocab?: { status: string[]; region: string[]; credentialing_status: string[] };
  required: string[];
  labels: Record<string, string>;
  authority: 'EMERGE';
}

export interface BuildingProvenance {
  origin_system: 'MONDAY' | 'EMERGE';
  origin_item_id: string | null;
  /** Monday items a person linked to the building in eMerge (evidence); never its origin. */
  monday_links?: string[];
  authority_system: 'EMERGE';
  /** "Created from Monday item …; eMerge is the authority", "Added in eMerge, linked to Monday item …; eMerge is the authority", … */
  text: string;
}

export interface Building {
  id: string;
  name: string;
  facility_type: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  county: string | null;
  licensed_beds: string | null;
  /** Every value (Monday's corporation is a list); null when none is recorded. */
  corporation: string[] | null;
  email: string | null;
  notes: string | null;
  /* The business, compliance and map fields (eMerge's since the 2026-10-05 ruling; edited here only). Optional: an older server omits them. */
  beds?: number | null;
  region?: string | null;
  /** The building's business status, in the TM board's words; Business Development reads it as a stage. */
  status?: string | null;
  /** Shown on the Scheduling board; it enforces nothing (CRED-RULING-001). */
  credentialing_status?: string | null;
  credentialing_enforced?: false;
  baa_sent?: boolean | null;
  /** YYYY-MM-DD, a calendar day. */
  baa_sent_date?: string | null;
  baa_signed?: boolean | null;
  baa_signed_date?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  version: number;
  merged: boolean;
  provenance: BuildingProvenance;
  /** The practices eMerge covers here (20261104f); practices_implied: nothing recorded, so the Audiology default. */
  practices?: PracticeCode[];
  practices_implied?: boolean;
}

/** One answer of the name search (GET /scheduling/buildings/search): a building eMerge already holds. */
export type MatchKind = 'EXACT' | 'SAME_NAME' | 'PREFIX' | 'ALL_WORDS' | 'SOME_WORDS';
export interface BuildingMatch {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  practices: PracticeCode[];
  practices_implied: boolean;
  /** On the Scheduling board's Main table already. */
  on_board: boolean;
  match_kind: MatchKind;
}
export interface BuildingSearch { q: string; limit: number; items: BuildingMatch[] }
/** POST /scheduling/buildings/:id/practices (or /end): what changed, and what was already as asked. */
export interface PracticeOutcome { building: Building; replayed: boolean; changed: PracticeCode[]; unchanged: PracticeCode[] }

/** The fields the writer takes on add (and edit), by the screen's names. */
export type BuildingField = 'name' | 'facility_type' | 'address' | 'city' | 'state' | 'zip_code' | 'county' | 'licensed_beds' | 'corporation' | 'email' | 'notes';
export const BUILDING_FIELDS: BuildingField[] = ['name', 'facility_type', 'address', 'city', 'state', 'zip_code', 'county', 'licensed_beds', 'corporation', 'email', 'notes'];
export const BUILDING_REQUIRED: BuildingField[] = ['name', 'facility_type', 'address', 'city', 'state', 'zip_code'];
export const BUILDING_LABEL: Record<BuildingField, string> = {
  name: 'Name', facility_type: 'Type', address: 'Street address', city: 'City', state: 'State', zip_code: 'ZIP',
  county: 'County', licensed_beds: 'Licensed beds', corporation: 'Corporation', email: 'Email', notes: 'Operational notes',
};

/** The edit panel's "Business, compliance and map" fields. */
export type DetailField = 'status' | 'region' | 'beds' | 'credentialing_status' | 'baa_sent' | 'baa_sent_date' | 'baa_signed' | 'baa_signed_date' | 'latitude' | 'longitude';
export const DETAIL_FIELDS: DetailField[] = ['status', 'region', 'beds', 'credentialing_status', 'baa_sent', 'baa_sent_date', 'baa_signed', 'baa_signed_date', 'latitude', 'longitude'];
export const DETAIL_LABEL: Record<DetailField, string> = {
  status: 'Business status', region: 'Region', beds: 'Beds (count)', credentialing_status: 'Credentialing (shown, not enforced)',
  baa_sent: 'BAA sent', baa_sent_date: 'BAA sent on', baa_signed: 'BAA signed', baa_signed_date: 'BAA signed on',
  latitude: 'Latitude (decimal degrees)', longitude: 'Longitude (decimal degrees)',
};
export type FormField = BuildingField | DetailField;

export interface DuplicateCandidate { id: string; name: string | null; city?: string | null; provenance?: string; matched_on: string[] }

export interface BuildingOutcome { building: Building; replayed: boolean; not_duplicate_of: { id: string; name: string | null; matched_on: string[] }[]; adopted_arrival?: boolean }

/* ── The facilities reconciliation report (GET /scheduling/buildings/reconciliation) ── */

export type ReconSectionKey = 'awaiting-adoption' | 'not-in-emerge' | 'differs' | 'gone-from-monday';
export interface ReconSection { key: ReconSectionKey; label: string; what: string; source: 'EMERGE' | 'MONDAY_MIRROR'; count: number | null; unavailable: string | null;
  /** Set when one facilities board has no completed mirror run: the rows are the other board's only, and the count is a floor. */
  provisional?: string | null }
export interface ReconDifference { field: string; label: string; emerge: string | string[] | null; monday: string | string[] | null }
export interface ReconRow {
  facility_id?: string;
  name?: string | null;
  monday_item_id?: string | null;
  board?: string;
  arrived_at?: string | null;
  first_seen_at?: string | null;
  /** The Monday item is linked to the building in eMerge (evidence), not its origin. */
  linked?: boolean;
  what?: string;
  differences?: ReconDifference[];
}
export interface ReconReport {
  generated_at: string;
  authority: 'EMERGE';
  mirror: { state: 'READ'; boards: { id: string; name: string; as_of: string | null; completed: boolean }[] } | { state: 'NO_POOL' | 'UNAVAILABLE'; reason: string };
  sections: ReconSection[];
  section: ReconSectionKey;
  page: number;
  limit: number;
  total: number | null;
  pages: number | null;
  past_end: boolean;
  message: string | null;
  /** The chosen section is provisional (one board not yet mirrored): why. */
  provisional?: string | null;
  rows: ReconRow[];
}

const withKey = (key: string) => ({ headers: { 'Idempotency-Key': key } });

export const buildingsApi = {
  meta: async (): Promise<ApiResult<BuildingMeta>> => (await api.get('/scheduling/buildings/meta')) as ApiResult<BuildingMeta>,
  get: async (id: string): Promise<ApiResult<Building>> => (await api.get(`/scheduling/buildings/${id}`)) as ApiResult<Building>,
  /** `fields.practices`: at least one (20261104f); region and status may be given on the add too. */
  create: async (fields: Partial<Record<FormField | 'monday_item_id', string>> & { practices?: PracticeCode[] }, key: string, notDuplicateOf?: string[]): Promise<ApiResult<BuildingOutcome>> =>
    (await api.post('/scheduling/buildings', { fields, not_duplicate_of: notDuplicateOf && notDuplicateOf.length ? notDuplicateOf : undefined }, withKey(key))) as ApiResult<BuildingOutcome>,
  /** "Is this building already ours?": buildings eMerge holds whose name matches, best first (at least 2 letters). */
  search: async (q: string, limit = 8): Promise<ApiResult<BuildingSearch>> =>
    (await api.get(`/scheduling/buildings/search?q=${encodeURIComponent(q)}&limit=${limit}`)) as ApiResult<BuildingSearch>,
  /** Add practices to a building eMerge holds. A practice already covered is reported in `unchanged`, never recorded twice. */
  addPractices: async (id: string, practices: PracticeCode[], key: string): Promise<ApiResult<PracticeOutcome>> =>
    (await api.post(`/scheduling/buildings/${encodeURIComponent(id)}/practices`, { practices }, withKey(key))) as ApiResult<PracticeOutcome>,
  /** End practices at a building; a reason is required. */
  endPractices: async (id: string, practices: PracticeCode[], reason: string, key: string): Promise<ApiResult<PracticeOutcome>> =>
    (await api.post(`/scheduling/buildings/${encodeURIComponent(id)}/practices/end`, { practices, reason }, withKey(key))) as ApiResult<PracticeOutcome>,
  update: async (id: string, fields: Partial<Record<FormField | 'monday_item_id', string | null>>, expectedVersion: number, key: string, notDuplicateOf?: string[]): Promise<ApiResult<BuildingOutcome>> =>
    (await api.patch(`/scheduling/buildings/${id}`, { fields, expected_version: expectedVersion, not_duplicate_of: notDuplicateOf && notDuplicateOf.length ? notDuplicateOf : undefined }, withKey(key))) as ApiResult<BuildingOutcome>,
  reconciliation: async (section: ReconSectionKey, page: number): Promise<ApiResult<ReconReport>> =>
    (await api.get(`/scheduling/buildings/reconciliation?section=${encodeURIComponent(section)}&page=${page}`)) as ApiResult<ReconReport>,
};

/** A fresh key per form opening; the duplicate confirmation re-sends under the same key. */
export const newBuildingKey = (): string => {
  const raw = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `bld-${raw}`;
};

/** The server's field errors (detail.fields), when it sent them. */
export function fieldErrorsOf(r: ApiResult<unknown>): Partial<Record<string, string>> {
  const d = r.detail as { fields?: Record<string, string> } | undefined;
  return d && d.fields && typeof d.fields === 'object' ? d.fields : {};
}

/** The duplicate guard's candidates (detail.candidates), when the server sent them. */
export function candidatesOf(r: ApiResult<unknown>): DuplicateCandidate[] {
  const d = r.detail as { candidates?: DuplicateCandidate[] } | undefined;
  return d && Array.isArray(d.candidates) ? d.candidates : [];
}
